import { useDashboardData } from '@/hooks/useDashboardData'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import { useState } from 'react'

const columnHelper = createColumnHelper<any>()

const columns = [
  columnHelper.accessor('tournament', {
    header: '大会',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('round', {
    header: '回戦',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('winner', {
    header: '勝利校',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('score', {
    header: 'スコア',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('loser', {
    header: '敗戦校',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('notes', {
    header: '備考',
    cell: (info) => info.getValue(),
  }),
]

const MatchesDataTable = () => {
  const { data, isLoading, error } = useDashboardData()
  const [sorting, setSorting] = useState<SortingState>([])

  // データが存在しない場合は空の配列を使用
  const matches = data?.tableView?.matches || []

  const table = useReactTable({
    data: matches,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-gray-200 rounded mb-2"></div>
        ))}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-red-600">
        データの読み込みに失敗しました
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-3 lg:px-6 py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() && (
                      <span className="ml-1">
                        {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="px-3 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-xs lg:text-sm text-gray-900"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      
      <div className="mt-4 text-xs lg:text-sm text-gray-700">
        総件数: {data.tableView.totalCount}件
      </div>
    </div>
  )
}

export default MatchesDataTable
